// WorkshopProfilePage.tsx - Workshop/Body Shop Dashboard
import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

interface DateRange {
  startDate: string;
  endDate: string;
}

interface WorkshopProfileData {
  workshop_name: string;
  claims_overview: {
    total_claims: number;
    active_claims: number;
    completed_claims: number;
    total_pending_parts: number;
    ready_claims: number;
  };
  recent_claims: {
    claim_number: string;
    claim_key: number;
    total_parts: number;
    delivered: number;
    pending: number;
    total_value: number;
    last_activity: string;
    status: string;
  }[];
  order_status: { status: string; count: number; value: number }[];
  supplier_reliability: {
    supplier_name: string;
    supplier_key: number;
    total_orders: number;
    delivery_rate: number;
    cancel_rate: number;
    avg_days: number;
    on_time_rate: number;
  }[];
  pending_deliveries: {
    claim_number: string;
    part_name: string;
    supplier_name: string;
    status: string;
    price: number;
    expected_date: string;
    urgency: string;
  }[];
  overdue_orders: {
    claim_number: string;
    part_name: string;
    supplier_name: string;
    status: string;
    deadline: string;
    days_overdue: number;
  }[];
  cost_summary: {
    total_cost: number;
    claim_count: number;
    avg_cost_per_claim: number;
    total_orders: number;
    avg_order_value: number;
    delivered_value: number;
    pending_value: number;
  };
  cost_trend: { week: string; orders: number; spend: number; claims: number }[];
  part_lead_times: {
    part_type: string;
    order_count: number;
    avg_lead_days: number;
    pending_count: number;
  }[];
  historical_performance: {
    week: string;
    claims: number;
    parts_per_claim: number;
    delivery_rate: number;
  }[];
  supplier_contacts: {
    supplier_key: number;
    supplier_name: string;
    supplier_code: string;
    order_count: number;
  }[];
  delivery_calendar: { date: string; deliveries_expected: number; value: number }[];
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);

const truncate = (str: string, len: number) =>
  str && str.length > len ? str.substring(0, len) + '...' : str;

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const urgencyColor = (urgency: string) => {
  switch (urgency) {
    case 'OVERDUE': return 'bg-red-100 text-red-800';
    case 'TODAY': return 'bg-orange-100 text-orange-800';
    case 'SOON': return 'bg-yellow-100 text-yellow-800';
    case 'SCHEDULED': return 'bg-green-100 text-green-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const statusColor = (status: string) => {
  switch (status) {
    case 'delivered': return 'bg-green-100 text-green-800';
    case 'in_transit': return 'bg-blue-100 text-blue-800';
    case 'ordered': return 'bg-purple-100 text-purple-800';
    case 'quoted': return 'bg-yellow-100 text-yellow-800';
    case 'cancelled': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const claimStatusColor = (status: string) => {
  switch (status) {
    case 'Complete': return 'bg-green-100 text-green-800';
    case 'In Progress': return 'bg-blue-100 text-blue-800';
    default: return 'bg-yellow-100 text-yellow-800';
  }
};

const WorkshopProfilePage: React.FC<{
  dateRange: DateRange;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
}> = ({ dateRange, authFetch }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<WorkshopProfileData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'claims' | 'deliveries' | 'suppliers' | 'costs'>('overview');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (dateRange.startDate) params.append('startDate', dateRange.startDate);
        if (dateRange.endDate) params.append('endDate', dateRange.endDate);

        const res = await authFetch(`/api/workshop/profile?${params}`);
        const json = await res.json();

        if (json.success) {
          setData(json.data);
        } else {
          setError(json.error || 'Failed to load data');
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateRange, authFetch]);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading workshop dashboard...</div>;
  if (error) return <div className="bg-red-50 border border-red-200 rounded p-4 text-red-800">{error}</div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No data available</div>;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'claims', label: 'My Claims' },
    { key: 'deliveries', label: 'Delivery Tracking' },
    { key: 'suppliers', label: 'Suppliers' },
    { key: 'costs', label: 'Costs' }
  ];

  const statusMap: Record<string, { label: string; color: string }> = {
    'delivered': { label: 'Delivered', color: '#22c55e' },
    'in_transit': { label: 'In Transit', color: '#3b82f6' },
    'ordered': { label: 'Ordered', color: '#8b5cf6' },
    'quoted': { label: 'Quoted', color: '#f59e0b' },
    'cancelled': { label: 'Cancelled', color: '#ef4444' }
  };

  const pieData = data.order_status.map(s => ({
    name: statusMap[s.status]?.label || s.status,
    value: s.count,
    color: statusMap[s.status]?.color || '#6b7280'
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Workshop Dashboard</h1>
            <p className="text-sm text-gray-500">{data.workshop_name}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">{data.claims_overview.active_claims}</div>
            <div className="text-sm text-gray-500">Active Claims</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-2">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{data.claims_overview.total_claims}</div>
              <div className="text-sm text-gray-500">Total Claims</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-orange-600">{data.claims_overview.active_claims}</div>
              <div className="text-sm text-gray-500">Active Claims</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-red-600">{data.claims_overview.total_pending_parts}</div>
              <div className="text-sm text-gray-500">Pending Parts</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{data.claims_overview.completed_claims}</div>
              <div className="text-sm text-gray-500">Completed</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-purple-600">{data.overdue_orders.length}</div>
              <div className="text-sm text-gray-500">Overdue Orders</div>
            </div>
          </div>

          {/* Order Status Pie + Overdue Alerts */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">Order Status</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {data.overdue_orders.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b bg-red-50">
                  <h3 className="font-semibold text-red-800">⚠️ Overdue Orders</h3>
                </div>
                <div className="p-4 max-h-64 overflow-y-auto">
                  {data.overdue_orders.slice(0, 5).map((o, i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b last:border-0">
                      <div>
                        <div className="font-medium text-sm">{truncate(o.part_name, 25)}</div>
                        <div className="text-xs text-gray-500">{o.claim_number} • {o.supplier_name}</div>
                      </div>
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-bold">
                        {o.days_overdue}d late
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Delivery Calendar */}
          {data.delivery_calendar.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">Upcoming Deliveries (Next 14 Days)</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.delivery_calendar}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.substring(5, 10)} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="deliveries_expected" fill="#3b82f6" name="Expected Deliveries" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Claims Tab */}
      {activeTab === 'claims' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">My Claims</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Claim</th>
                    <th className="px-4 py-2 text-center">Parts</th>
                    <th className="px-4 py-2 text-center">Delivered</th>
                    <th className="px-4 py-2 text-center">Pending</th>
                    <th className="px-4 py-2 text-right">Value</th>
                    <th className="px-4 py-2 text-center">Status</th>
                    <th className="px-4 py-2 text-left">Last Activity</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.recent_claims.map((c, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{truncate(c.claim_number, 15)}</td>
                      <td className="px-4 py-2 text-center">{c.total_parts}</td>
                      <td className="px-4 py-2 text-center text-green-600">{c.delivered}</td>
                      <td className="px-4 py-2 text-center text-orange-600">{c.pending}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(c.total_value)}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${claimStatusColor(c.status)}`}>{c.status}</span>
                      </td>
                      <td className="px-4 py-2 text-gray-500">{c.last_activity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Claim Progress Bars */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold mb-4">Claim Progress</h3>
            <div className="space-y-3">
              {data.recent_claims.slice(0, 10).map((c, i) => {
                const pct = c.total_parts > 0 ? (c.delivered / c.total_parts) * 100 : 0;
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{truncate(c.claim_number, 20)}</span>
                      <span className="text-gray-500">{c.delivered}/{c.total_parts} parts</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Deliveries Tab */}
      {activeTab === 'deliveries' && (
        <div className="space-y-6">
          {/* Pending Deliveries */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Pending Deliveries</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Claim</th>
                    <th className="px-4 py-2 text-left">Part</th>
                    <th className="px-4 py-2 text-left">Supplier</th>
                    <th className="px-4 py-2 text-center">Status</th>
                    <th className="px-4 py-2 text-right">Price</th>
                    <th className="px-4 py-2 text-left">Expected</th>
                    <th className="px-4 py-2 text-center">Urgency</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.pending_deliveries.map((d, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{truncate(d.claim_number, 12)}</td>
                      <td className="px-4 py-2">{truncate(d.part_name, 20)}</td>
                      <td className="px-4 py-2">{truncate(d.supplier_name, 15)}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${statusColor(d.status)}`}>{d.status}</span>
                      </td>
                      <td className="px-4 py-2 text-right">{formatCurrency(d.price)}</td>
                      <td className="px-4 py-2">{d.expected_date || '-'}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${urgencyColor(d.urgency)}`}>{d.urgency}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Overdue Orders */}
          {data.overdue_orders.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b bg-red-50">
                <h3 className="font-semibold text-red-800">Overdue Orders ({data.overdue_orders.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Claim</th>
                      <th className="px-4 py-2 text-left">Part</th>
                      <th className="px-4 py-2 text-left">Supplier</th>
                      <th className="px-4 py-2 text-left">Deadline</th>
                      <th className="px-4 py-2 text-right">Days Late</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.overdue_orders.map((o, i) => (
                      <tr key={i} className="hover:bg-red-50">
                        <td className="px-4 py-2 font-medium">{truncate(o.claim_number, 12)}</td>
                        <td className="px-4 py-2">{truncate(o.part_name, 20)}</td>
                        <td className="px-4 py-2">{truncate(o.supplier_name, 15)}</td>
                        <td className="px-4 py-2">{o.deadline}</td>
                        <td className="px-4 py-2 text-right">
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded font-bold">{o.days_overdue}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Suppliers Tab */}
      {activeTab === 'suppliers' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Supplier Reliability</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Supplier</th>
                    <th className="px-4 py-2 text-right">Orders</th>
                    <th className="px-4 py-2 text-right">Delivery %</th>
                    <th className="px-4 py-2 text-right">On-Time %</th>
                    <th className="px-4 py-2 text-right">Cancel %</th>
                    <th className="px-4 py-2 text-right">Avg Days</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.supplier_reliability.map((s, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{truncate(s.supplier_name, 25)}</td>
                      <td className="px-4 py-2 text-right">{s.total_orders}</td>
                      <td className="px-4 py-2 text-right text-green-600">{s.delivery_rate}%</td>
                      <td className="px-4 py-2 text-right text-blue-600">{s.on_time_rate || '-'}%</td>
                      <td className="px-4 py-2 text-right text-red-600">{s.cancel_rate}%</td>
                      <td className="px-4 py-2 text-right">{s.avg_days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Part Lead Times */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Part Lead Times by Type</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Part Type</th>
                    <th className="px-4 py-2 text-right">Orders</th>
                    <th className="px-4 py-2 text-right">Avg Lead Days</th>
                    <th className="px-4 py-2 text-right">Pending</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.part_lead_times.map((p, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{truncate(p.part_type, 25)}</td>
                      <td className="px-4 py-2 text-right">{p.order_count}</td>
                      <td className="px-4 py-2 text-right">{p.avg_lead_days}</td>
                      <td className="px-4 py-2 text-right text-orange-600">{p.pending_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Supplier Contacts */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Supplier Contacts</h3>
            </div>
            <div className="p-4 grid md:grid-cols-3 gap-4">
              {data.supplier_contacts.map((s, i) => (
                <div key={i} className="border rounded p-3">
                  <div className="font-medium">{s.supplier_name}</div>
                  <div className="text-sm text-gray-500">Code: {s.supplier_code || '-'}</div>
                  <div className="text-sm text-gray-500">{s.order_count} orders</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Costs Tab */}
      {activeTab === 'costs' && (
        <div className="space-y-6">
          {/* Cost KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{formatCurrency(data.cost_summary.total_cost)}</div>
              <div className="text-sm text-gray-500">Total Parts Cost</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{formatCurrency(data.cost_summary.delivered_value)}</div>
              <div className="text-sm text-gray-500">Delivered Value</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-orange-600">{formatCurrency(data.cost_summary.pending_value)}</div>
              <div className="text-sm text-gray-500">Pending Value</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-purple-600">{formatCurrency(data.cost_summary.avg_cost_per_claim)}</div>
              <div className="text-sm text-gray-500">Avg Cost/Claim</div>
            </div>
          </div>

          {/* Cost Trend */}
          {data.cost_trend.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">Weekly Cost Trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.cost_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.substring(5, 10)} />
                  <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="spend" fill="#3b82f6" name="Spend" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Historical Performance */}
          {data.historical_performance.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">Historical Performance</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={data.historical_performance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.substring(5, 10)} />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="claims" stroke="#3b82f6" name="Claims" />
                  <Line yAxisId="right" type="monotone" dataKey="parts_per_claim" stroke="#22c55e" name="Parts/Claim" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkshopProfilePage;